import { Box, Chip, Typography, useTheme } from '@mui/material'
import _ from 'lodash'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import ApiClient from '../../backend/ApiClient'
import { IContractDetails } from '../../definitions/Contract'
import { IKPICard } from '../../definitions/Shared'
import Helper from '../../helpers/Helper'
import TimeHelper from '../../helpers/TimeHelper'
import { useAppSelector } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import { BarChart, DonutChart, HorizontalBarChart } from '../charts'
import { DonutChartDefaultOptions } from '../charts/DonutChart'
import SmartCard from '../controls/cards/SmartCard'
import ChartCard from '../controls/data_visualization/ChartCard'
import KPICard from '../controls/data_visualization/KPICard'
import StaleObserver from '../controls/stale/StaleObserver'
import { SmartTableUtilities } from '../controls/table'
import SmartTable from '../controls/table/SmartTable'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'

interface P {
    contractId: number
}

export default function ContractAnalytics({ contractId }: P) {
    const dispatch = useDispatch()

    const [providerPerformanceOrder, setProviderPerformanceOrder] = useState<'worst' | 'best'>('worst')
    const [qmPerformanceOrder, setQMPerformanceOrder] = useState<'below' | 'meets'>('below')

    const time = useAppSelector(s => s.app.timeSettings ?? TimeHelper.timeSettings())
    const { status, response } = useQuery({
        queryKey: buildQueryKey('contract-detail', contractId, TimeHelper.stringify(time)),
        queryFn: () => ApiClient.getContractDetails(contractId, time),
        cache: true
    })

    const theme = useTheme()

    const defaultKPICard: IKPICard = { value: 0, df: { points: [] } }
    const totalCostOfCare = response?.totalCostOfCare ?? defaultKPICard
    const financialPosition = response?.financialPosition ?? defaultKPICard
    const attributedMembers = response?.attributedMembers ?? defaultKPICard
    const providerPerformance = response?.providerPerformance?.sort((x, y) => x.compositeScore - y.compositeScore) ?? []
    const qualityMeasures = response?.qualityMeasures ?? []
    const actualPMPM = response?.actualPMPM ?? { points: [] }
    const funds = response?.funds ?? []
    const claims = response?.claims ?? { amount: { points: [] }, count: { points: [] } }
    const ppChart = _.orderBy(providerPerformance, ['compositeScore'], providerPerformanceOrder === 'worst' ? 'asc' : 'desc')?.slice(0, 5)
    const qmPerformanceData = _.orderBy(qualityMeasures, ['grade', 'value'], qmPerformanceOrder === 'below' ? 'asc' : 'desc')?.slice(0, 5)
    const isLoading = status === 'loading'

    return (
        <Tile.Container>
            <Tile.Column spacing={2}>
                <Tile.Row flex={1} spacing={2}>
                    <SmartCard flex={1}>
                        <KPICard
                            data={totalCostOfCare}
                            inverse
                            isLoading={isLoading}
                            unit='$'
                            label='Total Cost of Care'
                            tooltipKey='total_cost_of_care'
                        />
                    </SmartCard>
                    <SmartCard flex={1}>
                        <KPICard
                            data={financialPosition}
                            isLoading={isLoading}
                            unit='$'
                            label='Financial Position'
                            tooltipKey='financial_position'
                        />
                    </SmartCard>
                    <SmartCard flex={1}>
                        <KPICard
                            data={Helper.dataframeToKPICard(actualPMPM)}
                            isLoading={isLoading}
                            unit='$'
                            inverse
                            label='Actual PMPM'
                            tooltipKey='actual_pmpm'
                        />
                    </SmartCard>
                    <SmartCard flex={1}>
                        <KPICard
                            data={attributedMembers}
                            isLoading={isLoading}
                            label='Attributed Members'
                            tooltipKey='attributed_members'
                        />
                    </SmartCard>
                </Tile.Row>
                <Tile.Row flex={1} spacing={2}>
                    <SmartCard flex={1} rows={4}>
                        {!isLoading && providerPerformance.length === 0
                            ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: 2, backgroundColor: theme.palette.background.default, borderRadius: 3 }}>
                                        <Typography>No Provider Performance Data Found</Typography>
                                    </Box>
                                )
                            : (
                                    <ChartCard
                                        title='Provider Performance'
                                        link={{ route: 'provider-performance-breakdown', params: { contractId: contractId } }}
                                        options={[
                                            { label: 'worst', onClick: () => setProviderPerformanceOrder('worst'), isActive: providerPerformanceOrder === 'worst' },
                                            { label: 'best', onClick: () => setProviderPerformanceOrder('best'), isActive: providerPerformanceOrder === 'best' }
                                        ]}
                                        chart={(
                                            <HorizontalBarChart
                                                isLoading={isLoading}
                                                data={{
                                                    datasets: [
                                                        { data: ppChart.map(x => x.compositeScore), backgroundColor: ppChart.map(x => x.compositeScore < 50 ? theme.palette.error[500] : x.compositeScore < 80 ? theme.palette.info.main : theme.palette.success[500]) }
                                                    ],
                                                    labels: ppChart.sort(x => x.compositeScore).map(x => _.truncate(x.providerName, { length: 20 }))?.slice(0, 5)
                                                }}
                                                onClickSerie={option => {
                                                    const providerId = ppChart.find(x => _.truncate(x.providerName, { length: 20 }) === option.seriesName).providerId
                                                    dispatch(changeRoute({ route: 'provider', params: { providerId: providerId, contractId: contractId } }))
                                                }}
                                            />
                                        )}
                                    />
                                )}
                    </SmartCard>
                    <SmartCard flex={1} rows={4}>
                        {!isLoading && qualityMeasures.length === 0
                            ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: 2, backgroundColor: theme.palette.background.default, borderRadius: 3 }}>
                                        <Typography>No Quality Measures Data Found</Typography>
                                    </Box>
                                )
                            : (
                                    <ChartCard
                                        title='Quality Measure Performance'
                                        link={{ route: 'quality-measures-breakdown', params: { contractId: contractId } }}
                                        options={[
                                            { label: 'below', onClick: () => setQMPerformanceOrder('below'), isActive: qmPerformanceOrder === 'below' },
                                            { label: 'meets', onClick: () => setQMPerformanceOrder('meets'), isActive: qmPerformanceOrder === 'meets' }
                                        ]}
                                        chart={(
                                            <Box overflow='auto'>
                                                <SmartTable
                                                    columns={[
                                                        { id: 'name', label: 'Name' },
                                                        { id: 'range', label: 'Acceptable Range', width: '10%' },
                                                        { id: 'value', label: 'Actual Value', width: '10%', numeric: true },
                                                        {
                                                            id: 'status', label: 'Status', customRenderer: cell => {
                                                                const val = cell.value as IContractDetails['qualityMeasures'][0]['grade']
                                                                const label = val === 'bad' ? 'Below Target' : val === 'good' ? 'Above Target' : 'Meets Target'
                                                                return <Chip size='small' label={label} color={val === 'good' ? 'success' : val === 'bad' ? 'error' : 'info'} />
                                                            }
                                                        }
                                                    ]}
                                                    sx={{
                                                        '& table': {
                                                            width: '100%',
                                                            tableLayout: 'fixed'
                                                        }
                                                    }}
                                                    hidePagination
                                                    isLoading={isLoading}
                                                    noCollapse
                                                    datasource={{
                                                        rows: qmPerformanceData?.map((x, i) => SmartTableUtilities.toRow([
                                                            { value: _.truncate(x.qualityMeasureName, { length: 20 }) },
                                                            { value: '> ' + _.minBy(x.range, x => x.min)?.min + '%' },
                                                            { value: x.value + '%' },
                                                            { value: x.grade }
                                                        ], i))
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    />
                                )}
                    </SmartCard>
                </Tile.Row>
                <Tile.Row flex={1} spacing={2}>
                    <SmartCard flex={1} rows={3}>
                        {!isLoading && funds?.length === 0
                            ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: 2, backgroundColor: theme.palette.background.default, borderRadius: 3 }}>
                                        <Typography>No Funds Data Found</Typography>
                                    </Box>
                                )
                            : (
                                    <ChartCard
                                        title='Fund Balances'
                                        link={{ route: 'funds-breakdown', params: { contractId: contractId } }}
                                        chart={(
                                            <DonutChart
                                                isLoading={isLoading}
                                                data={{
                                                    datasets: [
                                                        { data: funds?.map(x => x.balance) }
                                                    ],
                                                    labels: funds?.map(x => x.fundName)
                                                }}
                                                options={{ ...DonutChartDefaultOptions, plugins: { ...DonutChartDefaultOptions.plugins, legend: { position: 'right' } } }}
                                            />
                                        )}
                                    />
                                )}
                    </SmartCard>
                    <SmartCard flex={1} rows={3}>

                        <ChartCard
                            title='Claims Volume'
                            link={{ route: 'claims-breakdown', params: { contractId: contractId } }}
                            chart={(
                                <BarChart
                                    isLoading={isLoading}
                                    data={{
                                        datasets: [
                                            { data: claims?.count?.points?.map(x => x.y), label: 'Count', order: 1 },
                                            { data: claims?.amount?.points?.map(x => x.y), label: 'Amount', type: 'line' as any, order: 0, yAxisID: 'y2', borderColor: 'rgba(10, 164, 180, 1)', backgroundColor: 'rgba(10, 164, 180, 1)' }
                                        ],
                                        labels: claims?.count?.points?.map(x => x.x)
                                    }}
                                />
                            )}
                        />
                    </SmartCard>
                </Tile.Row>
            </Tile.Column>
            <StaleObserver status={status} />
        </Tile.Container>
    )
}
